/**
 * Types for the /admin/app-config definition sync: the repo-side manifest, the
 * live records aiWARE returns, and the diff between them.
 *
 * Two shapes are deliberately distinct. A ManifestDefinition is what we intend
 * an environment to have, so every field is present and non-null. A
 * LiveDefinition mirrors the GraphQL `ApplicationConfigDefinition` type, where
 * every field is nullable — including the ones we compare. diffDefinitions
 * treats a null there as "not supplied" rather than "differs", so an API that
 * omits `configType` cannot make every definition look drifted.
 *
 * LiveDefinition carries ONE PAIR OF FIELDS FROM ANOTHER TYPE: `value` and
 * `valueJSON` are on `ApplicationConfig`, not on `ApplicationConfigDefinition`,
 * and the scan merges them in by configKey. They are kept on the same record
 * because every consumer needs both halves of the same question — what the
 * environment defines, and what this organization set instead — and the
 * alternative is two parallel lists that can fall out of step.
 *
 * `appId` appears in neither: it is an environment/runtime fact, injected from
 * config.veritoneAppId at call time, not something the manifest pins.
 */

/** ApplicationConfigValueEnum in the aiWARE core schema. */
export type ConfigValueType =
  | 'String'
  | 'Boolean'
  | 'Date'
  | 'Integer'
  | 'JSON'
  | 'Float';

/** ApplicationConfigLevelEnum in the aiWARE core schema. */
export type ConfigLevel = 'Instance' | 'Organization' | 'User';

/** One entry of the manifest's `definitions` list — a definition we expect to exist. */
export interface ManifestDefinition {
  readonly configKey: string;
  readonly configType: ConfigValueType;
  readonly configLevel: ConfigLevel;
  readonly required: boolean;
  readonly secured: boolean;
  readonly description: string;
  /**
   * Absent where the definition genuinely declares no default (e.g. the JSON
   * configs). That is NOT the same as `''`, and the two compare differently.
   */
  readonly defaultValue?: string;
  /**
   * The JSON-typed counterpart. No manifest entry declares one today, but it is
   * a comparable field — so an environment that HAS one is reported as drifted
   * against our "declares none", which is the answer we want.
   */
  readonly defaultValueJSON?: unknown;
}

/** A key we have retired. It should not exist in any environment. */
export interface ManifestDeprecatedEntry {
  readonly configKey: string;
  readonly retiredIn?: string;
  readonly note: string;
}

export interface ConfigDefinitionManifest {
  readonly definitions: readonly ManifestDefinition[];
  /**
   * Developer/diagnostic toggles — ingestion paths, tile modes, thumbnail
   * timeouts. FULL DEFINITIONS, the same shape as `definitions`, and diffed
   * identically: one of these is still reported Missing or Drifted.
   *
   * A separate array rather than a flag on `definitions` so the set is visible
   * as a block in the manifest, and so moving a key in or out is a move rather
   * than an edit. It is NOT a weaker claim: one of these going missing is still
   * a finding, which is the whole reason the page reports it.
   */
  readonly developer: readonly ManifestDefinition[];
  /**
   * The middle tier: product flags an operator CAN reasonably change but most
   * never need to. Same claim as `definitions`, same diff; it decides which tab
   * lists the row and nothing else.
   */
  readonly advanced: readonly ManifestDefinition[];
  readonly deprecated: readonly ManifestDeprecatedEntry[];
}

/**
 * A record as returned by the `applicationConfigDefinition` query.
 *
 * `configKey` is nullable in the schema but required here on purpose: a record
 * without one cannot be bucketed, rendered with a stable React key, or deleted
 * (the delete input demands `configKey: String!`). `scanDefinitions` drops
 * those at the boundary so nothing downstream has to carry a null through.
 */
export interface LiveDefinition {
  readonly id?: string | null;
  readonly configKey: string;
  readonly configType?: ConfigValueType | null;
  readonly configLevel?: ConfigLevel | null;
  readonly required?: boolean | null;
  readonly secured?: boolean | null;
  readonly description?: string | null;
  readonly defaultValue?: string | null;
  /**
   * Where the platform stores a JSON-typed definition's default. A definition
   * carrying one is NOT a definition without a default, so it is compared in
   * its own right rather than folded into `defaultValue`: folding said a key
   * had drifted without saying which half of the default did.
   */
  readonly defaultValueJSON?: unknown;
  /**
   * THIS ORGANIZATION'S OWN VALUE, and not part of the definition at all.
   *
   * `value`/`valueJSON` do not exist on `ApplicationConfigDefinition` — they
   * live on `ApplicationConfig`, a different type behind a different query.
   * `scanDefinitions` reads both and merges the second onto the first by
   * configKey, so a row can say "the environment defines X, and this org has
   * overridden it with Y" without the page holding two parallel lists.
   *
   * Non-null in either means the organization is NOT taking the environment
   * default. Absent means it is — or that the values query could not be read,
   * which `valuesUnavailable` distinguishes.
   */
  readonly value?: string | null;
  readonly valueJSON?: unknown;
}

/**
 * The definition attributes that decide drift.
 *
 * Deliberately narrower than "everything a definition has". `configLevel`,
 * `required`, `secured` and `description` are all part of a definition and all
 * shown in the detail view, but a difference in them is not drift: they do not
 * change what any code reads. What a feature actually gets is decided by the
 * type and the default, so those are what the page reports on — and a
 * description that drifted from the manifest's wording is not a reason to put
 * an environment in the Drifted bucket and offer to write to it.
 */
export const COMPARABLE_FIELDS = [
  'configType',
  'defaultValue',
  'defaultValueJSON',
] as const;

export type ComparableField = (typeof COMPARABLE_FIELDS)[number];

/**
 * The detail view's two groups.
 *
 * The split is the point: everything in `GLOBAL_DETAIL_FIELDS` belongs to the
 * environment's definition and is what this page can write, while
 * `ORG_DETAIL_FIELDS` belongs to one organization and this page never touches
 * it. Reading them in one undifferentiated list is how an operator talks
 * themselves into "fixing" a default that an org had deliberately overridden.
 */
export const GLOBAL_DETAIL_FIELDS = [
  'configType',
  'defaultValue',
  'defaultValueJSON',
  'description',
  'configLevel',
  'required',
  'secured',
] as const;

/**
 * The global fields shown without asking. The rest sit behind the detail
 * view's "show more", and the split is by how often the answer differs.
 *
 * `defaultValueJSON` reads "none / none" on every definition that is not
 * JSON-typed. `configLevel`, `required` and `secured` are Organization / false
 * / false on all but a couple of keys — they used to be printed on every ROW,
 * which is three facts repeated thirty times to carry one that differs twice,
 * and the effect was to teach a reader to skip the line that held it. The
 * exception that matters, `configLevel: User`, is promoted to a pill on the row
 * instead; the values themselves stay here, one click away.
 */
export const GLOBAL_PRIMARY_FIELDS = [
  'configType',
  'defaultValue',
  'description',
] as const;

export const ORG_DETAIL_FIELDS = ['value', 'valueJSON'] as const;

export type GlobalDetailField = (typeof GLOBAL_DETAIL_FIELDS)[number];
export type OrgDetailField = (typeof ORG_DETAIL_FIELDS)[number];

export interface FieldDifference {
  readonly field: ComparableField;
  /** The manifest's value; `undefined` where the manifest declares none. */
  readonly expected: string | boolean | undefined;
  /** This environment's value; `undefined` where the definition declares none. */
  readonly actual: string | boolean | undefined;
  /**
   * Whether `applicationConfigDefinitionUpdate` can actually write this.
   *
   * The DAL builds its SET clause with truthy checks — `if (update.required)`,
   * `if (update.secured)`, `if (update.defaultValue)` — so a `false`, a `null`
   * or an empty string is dropped while the mutation still reports success.
   * Measured against stage us-gov-2 on 2026-09-17, not inferred: an update
   * sending `required:false, secured:false, defaultValue:null, description:"…"`
   * wrote the description and nothing else.
   */
  readonly writable: boolean;
  /**
   * Whether this difference decides anything.
   *
   * Only `defaultValueJSON` is ever `false`, and only on a definition that is
   * not `"JSON"`-typed: that column is where a JSON config keeps its default
   * and nothing reads it for any other type. An immaterial difference is still
   * carried and still marked in the detail view — in a colour that is not the
   * drift red — but it does not bucket the key as Drifted and does not block
   * Update. See `isMaterial` in diffDefinitions.
   */
  readonly material: boolean;
}

/** A manifest definition matched to a live record, with the comparison result. */
export interface ComparedDefinition {
  readonly manifest: ManifestDefinition;
  readonly live: LiveDefinition;
  readonly differences: readonly FieldDifference[];
  /**
   * Fields the live record left null, so nothing could be concluded about
   * them. Excluded from `differences` on purpose — see the file header.
   */
  readonly unknownFields: readonly ComparableField[];
  /**
   * True when every difference is one Update can write.
   *
   * Deliberately all-or-nothing. A partial update would fix the writable
   * fields, report success, and leave the row drifted on the rest — which
   * reads as "the button is broken" and is worse than not offering it. The
   * page shows the diff read-only instead, which is what the ticket asks for
   * as the minimum anyway.
   */
  readonly canUpdate: boolean;
}

export interface DeprecatedPresent {
  readonly entry: ManifestDeprecatedEntry;
  readonly live: LiveDefinition;
}

/**
 * How prominently a key is shown. ORTHOGONAL TO ITS BUCKET — a key of any tier
 * can be missing, drifted or in sync, and the page must still say so. The tier
 * decides which tab lists the row.
 */
export type DefinitionTier = 'standard' | 'advanced' | 'developer';

/** The five reported buckets. */
export interface DefinitionDiff {
  /**
   * Which manifest list each diffed key came from.
   *
   * Carried on the result rather than looked up from the manifest by the UI, so
   * `toRows` stays a pure function of its argument and the page never needs to
   * import the manifest to render a row. A map rather than one array per tier:
   * parallel arrays would let a key appear in two, which is the one thing
   * `validateManifest` exists to prevent.
   */
  readonly tierByKey: Readonly<Record<string, DefinitionTier>>;
  readonly missing: readonly ManifestDefinition[];
  readonly inSync: readonly ComparedDefinition[];
  readonly drifted: readonly ComparedDefinition[];
  readonly deprecatedPresent: readonly DeprecatedPresent[];
  readonly unrecognized: readonly LiveDefinition[];
  /**
   * Keys the environment returned more than once. The first record wins in the
   * buckets above; this records that the rest existed, because a duplicated
   * definition is itself something an admin wants to know about.
   */
  readonly duplicateKeys: readonly string[];
}

/**
 * Whether this session may run definition mutations.
 *
 * Four states. `checking` is the initial one and renders no banner at all —
 * it previously shared `unknown`, so every visit announced "could not confirm"
 * for as long as the query was in flight, which is a failure message for a
 * question nobody had asked yet.
 *
 * `unknown` means the root-org query failed or the cluster does not expose the
 * field — the page leaves its controls
 * enabled there and lets a refused call explain itself, because a disabled
 * button with no explanation is the worse outcome (and the permission model
 * may not be exactly "root org only" in every environment).
 */
export type RootOrgStatus = 'checking' | 'root' | 'notRoot' | 'unknown';

/** Which mutation a row's success marker is reporting. */
export type DefinitionSuccess = 'added' | 'updated' | 'deleted';

export interface AdminState {
  readonly isScanning: boolean;
  /** True once a scan has succeeded; until then an empty diff means nothing. */
  readonly hasScanned: boolean;
  /** The API's own message, verbatim. Never a rewritten one. */
  readonly scanError?: string;
  readonly scannedAt?: string;
  readonly liveDefinitions: readonly LiveDefinition[];
  /**
   * True when the definitions were read but this organization's values were
   * not. The page still works — it just cannot say which keys the org has
   * overridden, and it says THAT rather than showing every row as taking the
   * default, which would be a confident wrong answer.
   */
  readonly valuesUnavailable: boolean;
  readonly rootOrgStatus: RootOrgStatus;
  readonly organizationName?: string;
  /** Config keys with a mutation in flight, so their row can say so. */
  readonly pendingKeys: readonly string[];
  /** Per-key API error text. Errors belong on the row that failed. */
  readonly errorsByKey: Readonly<Record<string, string>>;
  /**
   * Keys whose mutation just SUCCEEDED, and what succeeded.
   *
   * Transient, and the reason it exists is that success here is invisible:
   * every write is followed by a re-scan, so a registered key stops being
   * Missing and reappears under In sync. The row does not confirm anything —
   * it vanishes from where you were looking and turns up somewhere else,
   * which reads as the click having thrown the row away.
   *
   * Cleared on a timer by `watchClearDefinitionSuccess`, so it survives the
   * re-scan that moves the row and the operator can follow it.
   */
  readonly successByKey: Readonly<Record<string, DefinitionSuccess>>;
}

/**
 * Payload shared by every mutation failure.
 *
 * Plural because "Add all missing" is a single call: when it fails there is no
 * one row to blame, and every row it was meant to fix has to say so.
 */
export interface KeyedFailure {
  readonly configKeys: readonly string[];
  readonly message: string;
}
